/**
 * help-widget.js — floating "?" button that opens a help search panel.
 *
 * Sprint 23 W6+ — connects the CRM to the public support site
 * https://support.magellania.net (mkdocs Material build).
 *
 * Behavior:
 *   - Mounts a fixed-position "?" button in the bottom-right corner
 *   - Click → opens a popover with:
 *       • search input (Enter or click → opens search results on support site)
 *       • quick links to common sections (Clients, Tripster, TeamUp, …)
 *       • "Связаться с поддержкой" link
 *   - All links open in a new tab (so the user doesn't lose CRM state)
 *
 * Mounted once during bootstrap (shell.js). Self-contained — no external CSS.
 */

const SUPPORT_BASE = 'https://support.magellania.net';

// Quick links shown in the help panel. Each entry has icon (lucide), label, path.
const QUICK_LINKS = [
    { icon: 'rocket',          label: 'Начало работы',     path: '/ru/user-guide/getting-started/' },
    { icon: 'users-round',     label: 'Клиенты (фильтры, теги)', path: '/ru/user-guide/clients/' },
    { icon: 'clipboard-list',  label: 'Работа со сметами', path: '/ru/user-guide/working-with-estimates/' },
    { icon: 'plug',            label: 'Tripster интеграция',  path: '/ru/user-guide/integrations/tripster/' },
    { icon: 'calendar',        label: 'TeamUp интеграция',    path: '/ru/user-guide/integrations/teamup/' },
    { icon: 'help-circle',     label: 'FAQ и проблемы',    path: '/ru/user-guide/troubleshooting/' }
];

const t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

function buildPanelHtml() {
    return `
        <div id="crm-help-fab" role="button" aria-label="Помощь" title="Помощь (F1)">
            <i data-lucide="help-circle" style="width:22px;height:22px"></i>
        </div>
        <div id="crm-help-panel" role="dialog" aria-modal="false" aria-labelledby="crm-help-title" hidden>
            <div id="crm-help-header">
                <h3 id="crm-help-title">Помощь</h3>
                <button id="crm-help-close" type="button" aria-label="Закрыть">✕</button>
            </div>
            <form id="crm-help-search-form" role="search">
                <div id="crm-help-search-row">
                    <i data-lucide="search" id="crm-help-search-icon"></i>
                    <input id="crm-help-search-input" type="search"
                           placeholder="Поиск в документации..."
                           autocomplete="off" spellcheck="false" />
                </div>
                <small id="crm-help-search-hint">Enter — открыть результаты на support.magellania.net</small>
            </form>
            <div id="crm-help-quick-section">
                <div id="crm-help-quick-title">Популярные разделы</div>
                <ul id="crm-help-quick-list">
                    ${QUICK_LINKS.map(l => `
                        <li>
                            <a href="${SUPPORT_BASE}${l.path}" target="_blank" rel="noopener" class="crm-help-link">
                                <i data-lucide="${l.icon}" class="crm-help-link-icon"></i>
                                <span>${l.label}</span>
                                <i data-lucide="external-link" class="crm-help-link-ext"></i>
                            </a>
                        </li>
                    `).join('')}
                </ul>
            </div>
            <div id="crm-help-contact">
                <a href="${SUPPORT_BASE}/support/" target="_blank" rel="noopener" class="crm-help-contact-link">
                    <i data-lucide="message-circle" style="width:14px;height:14px;vertical-align:-2px;margin-right:5px"></i>
                    Связаться с поддержкой
                </a>
            </div>
        </div>
    `;
}

function injectStyles() {
    if (document.getElementById('crm-help-widget-styles')) return;
    const style = document.createElement('style');
    style.id = 'crm-help-widget-styles';
    style.textContent = `
        #crm-help-fab {
            position: fixed; bottom: 20px; right: 20px; z-index: 9998;
            width: 48px; height: 48px; border-radius: 50%;
            background: var(--primary, #2563eb); color: white;
            display: flex; align-items: center; justify-content: center;
            cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.18);
            transition: transform .15s ease, box-shadow .15s ease;
        }
        #crm-help-fab:hover { transform: scale(1.06); box-shadow: 0 6px 18px rgba(0,0,0,0.25); }
        #crm-help-fab:active { transform: scale(0.98); }

        #crm-help-panel {
            position: fixed; bottom: 80px; right: 20px; z-index: 9999;
            width: 360px; max-width: calc(100vw - 40px); max-height: calc(100vh - 120px);
            background: var(--bg-surface, #ffffff); color: var(--text, #1f2937);
            border-radius: 12px; box-shadow: 0 12px 36px rgba(0,0,0,0.18);
            display: flex; flex-direction: column; overflow: hidden;
            animation: crm-help-pop .18s ease;
        }
        @keyframes crm-help-pop {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0); }
        }

        #crm-help-header {
            display: flex; align-items: center; justify-content: space-between;
            padding: 14px 16px; border-bottom: 1px solid var(--border, #e5e7eb);
        }
        #crm-help-header h3 {
            margin: 0; font-size: 16px; font-weight: 600;
        }
        #crm-help-close {
            background: none; border: 0; cursor: pointer; font-size: 18px;
            color: var(--text-muted, #6b7280); padding: 4px 8px;
        }
        #crm-help-close:hover { color: var(--text, #1f2937); }

        #crm-help-search-form {
            padding: 12px 16px 4px;
        }
        #crm-help-search-row {
            display: flex; align-items: center; gap: 8px;
            border: 1px solid var(--border, #d1d5db); border-radius: 8px;
            padding: 8px 10px; transition: border-color .15s, box-shadow .15s;
        }
        #crm-help-search-row:focus-within {
            border-color: var(--primary, #2563eb);
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
        }
        #crm-help-search-icon {
            width: 16px; height: 16px; color: var(--text-muted, #6b7280); flex-shrink: 0;
        }
        #crm-help-search-input {
            flex: 1; border: 0; outline: none; background: transparent;
            font-size: 14px; color: inherit; font-family: inherit;
        }
        #crm-help-search-hint {
            display: block; margin-top: 6px; font-size: 11px; color: var(--text-muted, #6b7280);
        }

        #crm-help-quick-section {
            padding: 12px 16px 8px;
        }
        #crm-help-quick-title {
            font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
            color: var(--text-muted, #6b7280); margin-bottom: 6px;
        }
        #crm-help-quick-list {
            list-style: none; margin: 0; padding: 0;
        }
        #crm-help-quick-list li {
            margin: 0;
        }
        .crm-help-link {
            display: flex; align-items: center; gap: 10px;
            padding: 8px 10px; border-radius: 6px;
            text-decoration: none; color: inherit; font-size: 13px;
            transition: background .12s;
        }
        .crm-help-link:hover {
            background: var(--hover-bg, #f3f4f6);
        }
        .crm-help-link-icon {
            width: 16px; height: 16px; color: var(--primary, #2563eb); flex-shrink: 0;
        }
        .crm-help-link span { flex: 1; }
        .crm-help-link-ext {
            width: 12px; height: 12px; color: var(--text-muted, #9ca3af); flex-shrink: 0;
        }

        #crm-help-contact {
            border-top: 1px solid var(--border, #e5e7eb);
            padding: 12px 16px;
        }
        .crm-help-contact-link {
            display: inline-flex; align-items: center;
            font-size: 13px; color: var(--primary, #2563eb); text-decoration: none;
        }
        .crm-help-contact-link:hover { text-decoration: underline; }

        /* Dark theme support */
        @media (prefers-color-scheme: dark) {
            #crm-help-panel {
                background: #1f2937; color: #e5e7eb;
            }
            #crm-help-header { border-bottom-color: #374151; }
            #crm-help-search-row { border-color: #4b5563; background: #111827; }
            .crm-help-link:hover { background: #374151; }
            #crm-help-contact { border-top-color: #374151; }
        }

        /* Mobile: full-width panel */
        @media (max-width: 600px) {
            #crm-help-panel {
                left: 10px; right: 10px; width: auto;
                bottom: 75px;
            }
            #crm-help-fab { bottom: 15px; right: 15px; }
        }
    `;
    document.head.appendChild(style);
}

function openPanel() {
    const panel = document.getElementById('crm-help-panel');
    if (!panel) return;
    panel.hidden = false;
    // Re-render lucide icons inside the panel (might have been re-injected)
    if (window.lucide?.createIcons) window.lucide.createIcons();
    // Focus search input for keyboard users
    setTimeout(() => {
        document.getElementById('crm-help-search-input')?.focus();
    }, 60);
}

function closePanel() {
    const panel = document.getElementById('crm-help-panel');
    if (panel) panel.hidden = true;
}

function togglePanel() {
    const panel = document.getElementById('crm-help-panel');
    if (!panel) return;
    panel.hidden ? openPanel() : closePanel();
}

function handleSearchSubmit(e) {
    e.preventDefault();
    const q = document.getElementById('crm-help-search-input').value.trim();
    if (!q) {
        window.open(SUPPORT_BASE + '/', '_blank', 'noopener');
        return;
    }
    // mkdocs Material search: /?q=... (the search page handles URL params)
    const url = `${SUPPORT_BASE}/?q=${encodeURIComponent(q)}`;
    window.open(url, '_blank', 'noopener');
}

export function mountHelpWidget() {
    // Avoid double-mount
    if (document.getElementById('crm-help-fab')) return;

    injectStyles();

    const container = document.createElement('div');
    container.id = 'crm-help-widget-root';
    container.innerHTML = buildPanelHtml();
    document.body.appendChild(container);

    // Re-render lucide icons after inject
    if (window.lucide?.createIcons) window.lucide.createIcons();

    document.getElementById('crm-help-fab').addEventListener('click', togglePanel);
    document.getElementById('crm-help-close').addEventListener('click', closePanel);
    document.getElementById('crm-help-search-form').addEventListener('submit', handleSearchSubmit);

    // Close on Escape, open on F1
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closePanel();
        if (e.key === 'F1') { e.preventDefault(); togglePanel(); }
    });

    // Close when clicking outside the panel (but not on the FAB itself)
    document.addEventListener('click', (e) => {
        const panel = document.getElementById('crm-help-panel');
        const fab = document.getElementById('crm-help-fab');
        if (!panel || panel.hidden) return;
        if (panel.contains(e.target) || fab?.contains(e.target)) return;
        closePanel();
    });

    // Re-i18n the title if i18n changes
    document.addEventListener('i18n:changed', () => {
        const title = document.getElementById('crm-help-title');
        if (title) title.textContent = t('nav.help', 'Помощь');
        const fab = document.getElementById('crm-help-fab');
        if (fab) fab.title = t('nav.help', 'Помощь') + ' (F1)';
    });
}
